/**
 * Production checkpoints + auto-draft API — PROD-001A.
 * Backend: app/api/routers/production_checkpoints.py.
 */
import apiClient from '@/shared/api/client'

export const CHECKPOINT_STATUSES = [
  'DRAFT', 'IN_PROGRESS', 'SUBMITTED', 'APPROVED', 'LOCKED', 'MISSED',
] as const
export type CheckpointStatus = (typeof CHECKPOINT_STATUSES)[number]

export const EXCEPTION_REASONS = [
  'MACHINE_BREAKDOWN', 'POWER_FAILURE', 'MATERIAL_SHORTAGE',
  'WORKER_SHORTAGE', 'QUALITY_ISSUE', 'SETTING_CHANGE',
  'CLEANING', 'TRIAL_RUN', 'COUNT_CHANGE', 'OTHER',
] as const
export type ExceptionReason = (typeof EXCEPTION_REASONS)[number]

export const STATUS_COLOR: Record<CheckpointStatus, string> = {
  DRAFT: 'default',
  IN_PROGRESS: 'processing',
  SUBMITTED: 'cyan',
  APPROVED: 'green',
  LOCKED: 'purple',
  MISSED: 'red',
}

export const REASON_LABEL: Record<ExceptionReason, string> = {
  MACHINE_BREAKDOWN: 'Machine breakdown',
  POWER_FAILURE: 'Power failure',
  MATERIAL_SHORTAGE: 'Material shortage',
  WORKER_SHORTAGE: 'Worker shortage',
  QUALITY_ISSUE: 'Quality issue',
  SETTING_CHANGE: 'Setting change',
  CLEANING: 'Cleaning',
  TRIAL_RUN: 'Trial run',
  COUNT_CHANGE: 'Count change',
  OTHER: 'Other',
}

// ─── Types ──────────────────────────────────────────────────────────

export interface DraftRow {
  id: string
  log_no: string
  machine_id: string | null
  machine_code: string | null
  machine_name: string | null
  department_code: string
  status: string
  production_type: string | null
  sales_order_id: string | null
  customer_id: string | null
  count_id: string | null
  laydown_id: string | null
  mixing_plan_id: string | null
  lot_no: string | null
  opening_stock_kg: number | null
  closing_stock_kg: number | null
  planned_output_kg: number | null
  expected_output_kg: number | null
  actual_output_kg: number | null
  efficiency_pct: number | null
  review_flags: { field: string; reason: string }[] | null
}

export interface Checkpoint {
  id: string
  production_log_id: string
  machine_id: string | null
  shift_id: string | null
  production_date: string
  checkpoint_no: number
  checkpoint_start_time: string
  checkpoint_end_time: string
  checkpoint_duration_minutes: number
  opening_stock_kg: number | null
  input_received_kg: number | null
  actual_output_kg: number | null
  transferred_qty_kg: number | null
  waste_kg: number | null
  rejection_kg: number | null
  adjustment_kg: number | null
  closing_stock_kg: number | null
  physical_closing_stock_kg: number | null
  variance_kg: number | null
  variance_pct: number | null
  expected_output_kg: number | null
  efficiency_pct: number | null
  waste_pct: number | null
  realization_pct: number | null
  runtime_minutes: number | null
  downtime_minutes: number | null
  idle_minutes: number | null
  power_fail_minutes: number | null
  operator_count: number | null
  exception_flag: boolean
  exception_reason: ExceptionReason | null
  exception_notes: string | null
  display_data: Record<string, unknown> | null
  data_source: string | null
  status: CheckpointStatus
  submitted_at: string | null
  submitted_by: string | null
  submitted_late: boolean
  approved_at: string | null
  approved_by: string | null
}

// Sprint carding-waste-categories — configurable carding waste category master
export interface CardingWasteCategory {
  id: string
  code: string
  name: string
  sort_order: number
  is_active: boolean
}

export interface FrequencyMaster {
  id: string
  plant_id: string | null
  department_id: string | null
  department_code: string
  machine_type: string | null
  checkpoint_interval_minutes: number
  checkpoints_per_shift: number
  start_from_shift_start: boolean
  active: boolean
  effective_from: string
  effective_to: string | null
  system_seeded: boolean
  admin_modified_at: string | null
  notes: string | null
}

export interface CheckpointSubmitBody {
  actual_output_kg?: number
  input_received_kg?: number
  transferred_qty_kg?: number
  waste_kg?: number
  rejection_kg?: number
  adjustment_kg?: number
  physical_closing_stock_kg?: number
  downtime_minutes?: number
  idle_minutes?: number
  power_fail_minutes?: number
  operator_count?: number
  exception_reason?: ExceptionReason
  exception_notes?: string
  display_data?: Record<string, unknown>
  data_source?: string
}

// ─── API ────────────────────────────────────────────────────────────

export const checkpointsApi = {
  generateDrafts: async (body: {
    plant_id: string
    log_date: string
    shift_id: string
    department_id?: string
  }) => (await apiClient.post<{
    generated: number
    drafts: DraftRow[]
  }>('/production/drafts/generate', body)).data,

  listDrafts: async (params: {
    plant_id: string
    log_date: string
    shift_id: string
    department_id?: string
  }) => (await apiClient.get<DraftRow[]>('/production/drafts', { params })).data,

  sweepOverdue: async (plant_id: string, grace_minutes = 15) =>
    (await apiClient.post<{ overdue_marked_missed: number }>(
      '/production/drafts/sweep-overdue',
      undefined,
      { params: { plant_id, grace_minutes } },
    )).data,

  listCheckpoints: async (log_id: string) =>
    (await apiClient.get<Checkpoint[]>(
      `/production-logs/${log_id}/checkpoints`,
    )).data,

  submitCheckpoint: async (id: string, body: CheckpointSubmitBody) =>
    (await apiClient.post<Checkpoint>(
      `/production/checkpoints/${id}/submit`, body,
    )).data,

  approveCheckpoint: async (id: string) =>
    (await apiClient.post<Checkpoint>(
      `/production/checkpoints/${id}/approve`,
    )).data,

  submitShift: async (log_id: string) =>
    (await apiClient.post<{
      log_id: string
      log_no: string
      submitted_checkpoint_nos: number[]
      skipped_missing_actuals: number[]
      skipped_already_done: number[]
      errors: { checkpoint_no?: number; error: string }[]
      log_status: string
    }>(`/production-logs/${log_id}/submit-shift`)).data,

  // Sprint 0139/0140 — BR mass-balance derive preview
  brDerivePreview: async (log_id: string) =>
    (await apiClient.get<{
      log_id: string
      br_machine_id: string | null
      card_machine_ids: string[]
      card_logs_found: number
      card_logs_pending: number
      card_output_kg: number
      card_waste_kg: number
      br_output_kg: number
      confidence: 'EXACT' | 'PARTIAL' | 'DATA_NOT_AVAILABLE'
      opening_kg: number | null
      input_kg: number | null
      br_waste_kg: number | null
      closing_kg_computed: number
      closing_kg_physical: number | null
      balance_closed: boolean
      // Sprint 0140 — suggestions for BR checkpoint auto-fill
      suggested_input_kg: number
      suggested_waste_kg: number
      // LAYDOWN_NET / OPERATOR_ENTRY / DERIVED_NEUTRAL
      input_source: string
      laydown_net_kg: number | null
    }>(`/production-logs/${log_id}/br-derive`)).data,

  // Sprint 0139/0140 — Per-card BR input share + realization % + waste suggestion
  cardingInputShare: async (log_id: string) =>
    (await apiClient.get<{
      log_id: string
      carding_machine_id: string | null
      br_machine_id: string | null
      br_input_kg: number | null
      br_waste_kg: number | null
      br_output_kg: number | null
      n_cards_on_line: number
      input_share_kg: number | null
      card_output_kg: number | null
      card_waste_kg: number | null
      realization_pct: number | null
      waste_pct_of_input: number | null
      unaccounted_kg: number | null
      confidence: 'EXACT' | 'PARTIAL' | 'DATA_NOT_AVAILABLE'
      source: string
      // Sprint 0140 — Carding waste suggestion (closes BR mass balance)
      standard_waste_pct: number | null
      suggested_waste_kg: number | null
      // Sprint 0147 — sliver hank Ne for LMW Mirror Kgs ↔ Hank auto-calc
      sliver_hank_ne: number | null
    }>(`/production-logs/${log_id}/carding-input-share`)).data,

  // Sprint 0140 — Carding section waste closure (end-of-shift)
  getCardingSectionWaste: async (params: {
    plant_id: string
    log_date: string  // YYYY-MM-DD
    shift_id: string
  }) => (await apiClient.get<{
    predicted_waste_kg: number
    n_cards: number
    card_breakdown: Array<{
      machine_id: string | null
      machine_code: string | null
      output_kg: number
      qc_waste_pct: number
      predicted_waste_kg: number
    }>
    threshold_pct: number
    saved: {
      id: string
      predicted_waste_kg: number
      actual_waste_kg: number
      variance_kg: number
      variance_pct: number
      threshold_pct_at_save: number
      breached_threshold: boolean
      entered_at: string | null
      entered_by: string | null
      notes: string | null
      gm_notification_id: string | null
      gm_acknowledged_at: string | null
      // Sprint 0198 — Blow Room + Carding section mass-balance reconciliation
      contamination_kg: number
      rejection_kg: number
      feed_kg: number | null
      output_kg: number | null
      balance_variance_kg: number | null
      balance_variance_pct: number | null
      balance_breached: boolean
      // Sprint carding-waste-categories — configurable per-category waste lines
      lines: Array<{ category_id: string; code: string; name: string; kg: number }>
    } | null
  }>('/production/carding-section-waste', { params })).data,

  saveCardingSectionWaste: async (payload: {
    plant_id: string
    log_date: string
    shift_id: string
    actual_waste_kg: number
    contamination_kg?: number
    rejection_kg?: number
    notes?: string
    // Sprint carding-waste-categories — when present, server derives actual_waste_kg from these
    lines?: Array<{ category_id: string; kg: number }>
  }) => (await apiClient.post<{
    id: string
    predicted_waste_kg: number
    actual_waste_kg: number
    variance_kg: number
    variance_pct: number
    threshold_pct_at_save: number
    breached_threshold: boolean
    gm_notification_id: string | null
    // Sprint 0198 — Blow Room + Carding section mass-balance reconciliation
    contamination_kg: number
    rejection_kg: number
    feed_kg: number | null
    output_kg: number | null
    balance_variance_kg: number | null
    balance_variance_pct: number | null
    balance_breached: boolean
  }>('/production/carding-section-waste', payload)).data,

  // Sprint carding-waste-categories — configurable carding waste category master
  getCardingWasteCategories: async (params?: { active_only?: boolean }) =>
    (await apiClient.get<CardingWasteCategory[]>(
      '/production/carding-waste-categories', { params },
    )).data,

  createCardingWasteCategory: async (body: { code: string; name: string; sort_order?: number }) =>
    (await apiClient.post<CardingWasteCategory>(
      '/production/carding-waste-categories', body,
    )).data,

  updateCardingWasteCategory: async (id: string, body: { name?: string; sort_order?: number; is_active?: boolean }) =>
    (await apiClient.patch<CardingWasteCategory>(
      `/production/carding-waste-categories/${id}`, body,
    )).data,

  // Frequency master
  listFrequencies: async (params: {
    plant_id?: string
    department_code?: string
    active_only?: boolean
  } = {}) => (await apiClient.get<FrequencyMaster[]>(
    '/production/checkpoint-frequency', { params },
  )).data,

  createFrequency: async (body: Omit<FrequencyMaster, 'id' | 'active' | 'system_seeded' | 'admin_modified_at'>) =>
    (await apiClient.post<FrequencyMaster>(
      '/production/checkpoint-frequency', body,
    )).data,

  updateFrequency: async (id: string, body: Partial<FrequencyMaster>) =>
    (await apiClient.patch<FrequencyMaster>(
      `/production/checkpoint-frequency/${id}`, body,
    )).data,

  // Sprint 0146 QE merge — fault entries on the production log (shift-
  // level, attached to the parent log, NOT individual checkpoints).
  // Upsert semantics: replaces all rows for the log on each save so a
  // re-submit of the same payload doesn't accumulate duplicates.
  listLogFaults: async (log_id: string) =>
    (await apiClient.get<Array<{
      fault_type: 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK'
                | 'CHUTE_FAULTS' | 'DOFFS'
      count: number
      root_cause: string | null
      corrective_action: string | null
    }>>(
      `/production-logs/${log_id}/faults`,
    )).data,

  upsertLogFaults: async (log_id: string, faults: Array<{
    fault_type: 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK'
              | 'CHUTE_FAULTS' | 'DOFFS'
    count: number
    root_cause?: string | null
    corrective_action?: string | null
  }>) =>
    (await apiClient.post<{ log_id: string; fault_entries: unknown[] }>(
      `/production-logs/${log_id}/faults`,
      { faults },
    )).data,
}
